// src/components/common/ClientsSlider.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Chip,
  Paper,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import styles from "./ClientsSlider.module.css";

// Clients Data
const clientsData = [
  { id: 1, name: "TechCorp International", industry: "Technology", logo: "🏢" },
  { id: 2, name: "MediHealth Systems", industry: "Healthcare", logo: "🏥" },
  { id: 3, name: "EduGlobal Academy", industry: "Education", logo: "🎓" },
  { id: 4, name: "FinSecure Bank", industry: "Finance", logo: "🏦" },
  {
    id: 5,
    name: "TransFast Logistics",
    industry: "Transportation",
    logo: "🚚",
  },
  { id: 6, name: "MediaWave Studios", industry: "Entertainment", logo: "🎬" },
  { id: 7, name: "GreenEnergy Solutions", industry: "Energy", logo: "🌱" },
  { id: 8, name: "SmartRetail Group", industry: "Retail", logo: "🛒" },
];

const ClientsSlider = () => {
  const theme = useTheme();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // ✅ Hooks called at top level (not conditionally)
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Determine slides to show based on screen size
  let slidesToShow = 4;
  if (isMobile) {
    slidesToShow = 1;
  } else if (isTablet) {
    slidesToShow = 2;
  }

  // Duplicate slides for infinite loop
  const duplicatedSlides = [...clientsData, ...clientsData, ...clientsData];

  // Auto-slide
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => prev + 1);
    }, 3000);
    return () => clearInterval(timer);
  }, [isPaused]);

  // Reset when reaching the end
  useEffect(() => {
    if (currentSlide >= clientsData.length) {
      setTimeout(() => {
        setCurrentSlide(0);
      }, 50);
    }
  }, [currentSlide]);

  const getCurrentIndex = () => {
    return currentSlide % clientsData.length;
  };

  const handlePrev = () => {
    if (currentSlide === 0) {
      setCurrentSlide(clientsData.length - 1);
    } else {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleNext = () => {
    setCurrentSlide(currentSlide + 1);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <Box className={styles.clientsSection}>
      <Container maxWidth="xl">
        {/* Section Header */}
        <Box className={styles.clientsHeader}>
          <Chip
            label="🤝 Trusted Partners"
            className={styles.clientsChip}
            size="small"
          />
          <Typography
            variant="h2"
            className={styles.clientsTitle}
            sx={{
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
            }}
          >
            Our <span className={styles.highlight}>Clients</span>
          </Typography>
          <Typography variant="body1" className={styles.clientsSubtitle}>
            Trusted by leading organisations worldwide
          </Typography>
        </Box>

        {/* Slider */}
        <Box className={styles.sliderWrapper}>
          <Box
            className={styles.sliderContainer}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <Box
              className={styles.sliderTrack}
              style={{
                transform: `translateX(-${currentSlide * (100 / slidesToShow)}%)`,
                transition: "transform 500ms ease-in-out",
              }}
            >
              {duplicatedSlides.map((client, index) => (
                <Box key={index} className={styles.slideItem}>
                  <Paper elevation={0} className={styles.clientCard}>
                    <Box className={styles.clientLogo}>{client.logo}</Box>
                    <Typography variant="body2" className={styles.clientName}>
                      {client.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      className={styles.clientIndustry}
                    >
                      {client.industry}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Slider Controls */}
          <Box className={styles.sliderControls}>
            <IconButton
              className={styles.sliderArrow}
              onClick={handlePrev}
              aria-label="Previous"
            >
              <ArrowBack />
            </IconButton>
            <Box className={styles.sliderDots}>
              {clientsData.map((_, index) => (
                <button
                  key={index}
                  className={`${styles.sliderDot} ${
                    getCurrentIndex() === index ? styles.sliderDotActive : ""
                  }`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </Box>
            <IconButton
              className={styles.sliderArrow}
              onClick={handleNext}
              aria-label="Next"
            >
              <ArrowForward />
            </IconButton>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ClientsSlider;
